
import React, { useState, useEffect } from 'react';
import { 
  Zap, 
  Play, 
  Square, 
  RotateCcw, 
  Activity, 
  Clock, 
  Server, 
  AlertCircle, 
  CheckCircle2, 
  RefreshCw,
  MoreHorizontal,
  ChevronRight,
  Database
} from 'lucide-react';

interface Operation {
  id: string;
  name: string;
  lastAction: string;
  status: 'ready' | 'working' | 'stopped' | 'error';
  successRate: number;
  type: string;
}

const INITIAL_OPERATIONS: Operation[] = [
  { id: '1', name: 'Sync HubSpot Vendas', lastAction: 'Hoje, 14:32', status: 'working', successRate: 99.8, type: 'CRM-Push' },
  { id: '2', name: 'Lead Pull Facebook Ads', lastAction: 'Hoje, 14:30', status: 'ready', successRate: 100, type: 'Marketing-Web' },
  { id: '3', name: 'RD Station Webhooks', lastAction: 'Ontem, 23:55', status: 'stopped', successRate: 94.2, type: 'API-Listen' },
  { id: '4', name: 'Extractor Imobiliária Sul', lastAction: '10/05, 08:00', status: 'error', successRate: 0, type: 'Legacy-Sync' },
  { id: '5', name: 'WhatsApp Bot Router', lastAction: 'Hoje, 14:33', status: 'working', successRate: 98.5, type: 'Messaging' },
];

export const LeadsPage: React.FC<{ darkMode: boolean }> = ({ darkMode }) => {
  const [operations, setOperations] = useState<Operation[]>(INITIAL_OPERATIONS);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 1500);
  };

  const updateStatus = (id: string, newStatus: Operation['status']) => {
    setOperations(prev => prev.map(op => 
      op.id === id ? { ...op, status: newStatus, lastAction: newStatus === 'working' ? 'Agora' : op.lastAction } : op
    ));
  };

  const StatusBadge = ({ status }: { status: Operation['status'] }) => {
    switch (status) {
      case 'working':
        return (
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"></span>
            </span>
            <span className={`text-xs font-black uppercase tracking-wider ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Processando</span>
          </div>
        );
      case 'ready':
        return (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.4)]"></div>
            <span className={`text-xs font-black uppercase tracking-wider ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Pronto</span>
          </div>
        );
      case 'stopped':
        return (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-gray-500"></div>
            <span className={`text-xs font-black uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Parado</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,44,44,0.5)] animate-pulse"></div>
            <span className={`text-xs font-black uppercase tracking-wider ${darkMode ? 'text-red-400' : 'text-red-700'}`}>Erro Crítico</span>
          </div>
        );
    }
  };

  return (
    <div className={`p-6 md:p-8 min-h-full animate-in fade-in duration-500 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className={`text-2xl font-black flex items-center gap-3 ${darkMode ? 'text-white' : 'text-vision-green'}`}>
            <Zap className={darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'} />
            Lead Processing Hub
          </h1>
          <p className={`text-sm mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Controle de manipuladores, webhooks e sincronização com CRMs externos.
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-md active:scale-95
            ${darkMode ? 'bg-vision-surface hover:bg-vision-border text-white border border-vision-border' : 'bg-white border border-gray-200 text-[#294b29] hover:bg-gray-50'}
          `}
        >
          <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
          {isRefreshing ? 'Sincronizando...' : 'Atualizar Status'}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`p-3 rounded-xl ${darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
            <Activity size={24} />
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold opacity-50 tracking-widest">Health Score</p>
            <p className="text-xl font-black">98.2%</p>
          </div>
        </div>
        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`p-3 rounded-xl ${darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
            <Server size={24} />
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold opacity-50 tracking-widest">Op. Ativas</p>
            <p className="text-xl font-black">{operations.filter(o => o.status === 'working').length}/{operations.length}</p>
          </div>
        </div>
        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`p-3 rounded-xl ${darkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
            <Database size={24} />
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold opacity-50 tracking-widest">Lead Throughput</p>
            <p className="text-xl font-black">1.4k <span className="text-xs font-normal opacity-50">/hr</span></p>
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className={`rounded-2xl border shadow-xl overflow-hidden ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className={`text-[10px] uppercase tracking-[0.2em] font-black ${darkMode ? 'bg-vision-surface text-gray-500' : 'bg-gray-50 text-gray-400'}`}>
                <th className="px-8 py-5">Operação</th>
                <th className="px-6 py-5">Tipo</th>
                <th className="px-6 py-5 flex items-center gap-2"><Clock size={12}/> Última Ação</th>
                <th className="px-6 py-5">Status</th>
                <th className="px-6 py-5 text-center">Taxa de Sucesso</th>
                <th className="px-8 py-5 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-100'}`}>
              {operations.map((op) => (
                <tr key={op.id} className={`group transition-all ${darkMode ? 'hover:bg-vision-surface/30' : 'hover:bg-gray-50'}`}>
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-bold text-lg border ${darkMode ? 'bg-black border-vision-border text-white' : 'bg-gray-100 border-gray-200 text-gray-700'}`}>
                        {op.name.charAt(0)}
                      </div>
                      <span className="font-bold text-sm">{op.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-6">
                    <span className={`text-[10px] font-black px-2 py-1 rounded-md border tracking-wider ${darkMode ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-600'}`}>
                      {op.type}
                    </span>
                  </td>
                  <td className="px-6 py-6 text-xs font-mono opacity-60">
                    {op.lastAction}
                  </td>
                  <td className="px-6 py-6">
                    <StatusBadge status={op.status} />
                  </td>
                  <td className="px-6 py-6 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`text-xs font-black ${op.successRate > 95 ? 'text-green-500' : op.successRate > 0 ? 'text-yellow-500' : 'text-red-500'}`}>
                        {op.successRate}%
                      </span>
                      <div className="w-16 h-1 bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-current opacity-50 transition-all duration-1000" style={{ width: `${op.successRate}%`, color: op.successRate > 95 ? '#22c55e' : '#eab308' }}></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {op.status === 'working' ? (
                        <button 
                          onClick={() => updateStatus(op.id, 'stopped')}
                          className="p-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all shadow-sm"
                          title="Parar"
                        >
                          <Square size={16} fill="currentColor" />
                        </button>
                      ) : (
                        <button 
                          onClick={() => updateStatus(op.id, 'working')}
                          className="p-2 rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500 hover:text-white transition-all shadow-sm"
                          title="Iniciar"
                        >
                          <Play size={16} fill="currentColor" />
                        </button>
                      )}
                      <button 
                        onClick={() => updateStatus(op.id, 'working')}
                        className={`p-2 rounded-lg border transition-all ${darkMode ? 'border-vision-border text-gray-400 hover:bg-white/10' : 'border-gray-200 text-gray-500 hover:bg-gray-100'}`}
                        title="Reiniciar"
                      >
                        <RotateCcw size={16} />
                      </button>
                      <button className={`p-2 rounded-lg ${darkMode ? 'text-gray-600 hover:text-white' : 'text-gray-300 hover:text-gray-600'}`}>
                        <MoreHorizontal size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Footer Info */}
        <div className={`p-4 border-t flex justify-between items-center text-[10px] font-bold uppercase tracking-widest ${darkMode ? 'border-vision-border bg-black/40 text-gray-600' : 'border-gray-100 bg-gray-50 text-gray-400'}`}>
          <div className="flex items-center gap-4">
             <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-green-500"/> API Online</span>
             <span className="flex items-center gap-1"><AlertCircle size={12} className="text-blue-500"/> 124 Webhooks Pendentes</span>
          </div>
          <div>
            System Version: v4.2.0-stable
          </div>
        </div>
      </div>
    </div>
  );
};
